import type { FieldGroupPropSize } from '@consta/uikit/FieldGroup'
import { FieldGroup } from '@consta/uikit/FieldGroup'
import { Text } from '@consta/uikit/Text'
import { useHandleInput } from '@library/hooks'
import { convertToNumber } from '@library/utils'
import cn from 'classnames'
import type { Nullable } from 'common/types'
import type { FC } from 'react'
import { useEffect, useId } from 'react'

import { Show } from '../Flow'
import { NumericInput } from '../NumericInput'
import css from './Interval.css'
import { IntervalValidation } from './validation'

export type IntervalValue = {
  from: Nullable<number> | undefined
  to: Nullable<number> | undefined
}

export type OnIntervalChange = (value: IntervalValue) => void

type IntervalProps = {
  containerClassName?: string
  min?: number
  max?: number
  valueFrom: Nullable<number> | undefined
  valueTo: Nullable<number> | undefined
  label?: string
  labelPosition?: 'left' | 'top'
  isOnlyInteger?: boolean
  allowEquality?: boolean
  showCaption?: boolean
  withInputStyle?: boolean
  size?: FieldGroupPropSize
  onChange: OnIntervalChange
  onValidation?: (isValid: boolean) => void
}

const isIntervalReadyToSync = ({ from, to }: IntervalValue): boolean =>
  from !== undefined && to !== undefined

export const Interval: FC<IntervalProps> = ({
  containerClassName,
  max = Number.POSITIVE_INFINITY,
  min = Number.NEGATIVE_INFINITY,
  valueFrom,
  valueTo,
  label,
  labelPosition,
  isOnlyInteger = false,
  allowEquality = false,
  showCaption,
  withInputStyle = true,
  size = 'xs',
  onChange,
  onValidation,
}) => {
  const validator = new IntervalValidation(
    { value: min, inclusive: true },
    { value: max, inclusive: true },
    allowEquality,
  )
  const { handle, state, validation, isValid } = useHandleInput({
    initialValue: { from: valueFrom, to: valueTo },
    isValidCheck: validator.validate,
    isReadyToSync: isIntervalReadyToSync,
    converters: { from: convertToNumber, to: convertToNumber },
    onChange,
  })

  useEffect(() => {
    onValidation?.(isValid)
  }, [isValid, onValidation])

  const className = containerClassName
    ? containerClassName
    : cn(css.intervalContainer, { [css.top]: labelPosition === 'top' })
  const mode = isOnlyInteger ? 'integer' : 'float'

  const fromId = useId()
  const toId = useId()

  return (
    <div className={className}>
      <Text view="secondary" size={size} as="label" htmlFor={fromId}>
        {label}
      </Text>
      <div className={css.container}>
        <FieldGroup size={size}>
          <NumericInput
            id={fromId}
            className={cn({ [css.inputField]: withInputStyle })}
            rightSide="мс"
            name="from"
            step={1}
            incrementButtons={false}
            value={state.from}
            mode={mode}
            status={validation.from ? 'alert' : undefined}
            onChange={handle('from')}
          />
          <NumericInput
            id={toId}
            className={cn({ [css.inputField]: withInputStyle })}
            rightSide="мс"
            name="to"
            step={1}
            incrementButtons={false}
            value={state.to}
            mode={mode}
            status={validation.to ? 'alert' : undefined}
            onChange={handle('to')}
          />
        </FieldGroup>
        <Show when={!!showCaption && !isValid}>
          <Text
            className={css.caption}
            size="xs"
            view="alert"
            as="label"
            htmlFor={validation.from ? fromId : toId}>
            {validation.from ?? validation.to}
          </Text>
        </Show>
      </div>
    </div>
  )
}
